/// <reference types="@angular/localize" />

import { Component, inject, OnInit } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { authenticationControllerLogoutV1 } from '@lexplus-authentication-api';
import { API_CONFIGURATION } from '@lexplus-environments';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { ImpersonationStateClear, notify, ProfileStateClear, TemporarySessionStateClear } from '@lexplus-shared-lib';
import { Store } from '@ngxs/store';
import { LocalDevAutoLoginService } from '../../services/local-dev-auto-login.service';

@Component({
  selector: 'lp-logout',
  imports: [],
  templateUrl: './logout.component.html',
  styleUrl: './logout.component.scss',
})
export class LogoutComponent implements OnInit {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly localDevAutoLogin = inject(LocalDevAutoLoginService);

  // performLogout handles its own failures, and logout.component.spec awaits this promise so
  // it can flush the HTTP call in between.
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  public async ngOnInit(): Promise<void> {
    await this.performLogout();
  }

  private async performLogout(): Promise<void> {
    try {
      await lastValueFrom(
        authenticationControllerLogoutV1(this.httpClient, this.apiConfiguration.baseUrlAuth, {
          body: {},
        }),
      );
    } catch (error) {
      // 401: the session had already expired, which is the logout's goal anyway.
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return;
      }
      notify.error($localize`:@@auth.logout.failed:Abmeldung fehlgeschlagen`, {
        description: $localize`:@@auth.logout.failedDescription:Bei der Abmeldung ist ein Fehler aufgetreten.`,
      });
    } finally {
      // Always clear the local state and navigate to the start page — even when the
      // logout call fails (the token may already have expired). ProfileStateClear
      // removes sensitive profile data (roles/permissions/name/e-mail) from the state
      // and localStorage, but keeps the appDeviceId (device trust for the 2FA skip).
      this.store.dispatch(new ProfileStateClear());
      this.store.dispatch(new TemporarySessionStateClear());
      this.store.dispatch(new ImpersonationStateClear());
      // Leaves the login form to the user on a localhost dev server; no-op everywhere else.
      this.localDevAutoLogin.suppress();
      await this.router.navigateByUrl('/');
    }
  }
}
